import React, { useState } from 'react';
import {
  Inbox, Archive, Clock, CheckCircle, Mail, MessageSquare, Phone,
  Smartphone, MessageCircle, Globe, ChevronDown, ChevronRight, Tag,
  LucideIcon, PanelLeftClose, PanelLeft,
  Brain, TrendingDown, ShieldAlert, Zap, AlertCircle
} from 'lucide-react';

interface View {
  id: string;
  label: string;
  icon: LucideIcon;
}

interface SmartView {
  id: string;
  label: string;
  icon: LucideIcon;
  color: string;       // tailwind color for selected state
  dotColor: string;    // accent dot color
}

interface TicketSidebarProps {
  selectedView?: string;
  onViewChange?: (viewId: string) => void;
  selectedTags?: string[];
  onTagToggle?: (tag: string) => void;
  selectedChannel?: string;
  onChannelChange?: (channel: string) => void;
  viewCounts?: {
    inbox: number; all: number; snoozed: number; closed: number;
    needs_response?: number; negative_sentiment?: number; flagged_review?: number;
  };
  availableTags?: string[];
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const VIEWS: View[] = [
  { id: 'inbox', label: 'Inbox', icon: Inbox },
  { id: 'all', label: 'All', icon: Mail },
  { id: 'snoozed', label: 'Snoozed', icon: Clock },
  { id: 'closed', label: 'Closed', icon: CheckCircle }
];

const AI_SMART_VIEWS: SmartView[] = [
  { id: 'needs_response', label: 'Needs Response', icon: AlertCircle, color: 'amber', dotColor: 'bg-amber-400' },
  { id: 'negative_sentiment', label: 'Negative Sentiment', icon: TrendingDown, color: 'red', dotColor: 'bg-red-400' },
  { id: 'flagged_review', label: 'Flagged for Review', icon: ShieldAlert, color: 'orange', dotColor: 'bg-orange-400' },
];

const DEFAULT_TAGS = [
  'urgent', 'returns', 'vip', 'cancellation', 'refunds',
  'damaged', 'order-status', 'not-shipped', 'not-received',
  'escalated', 'shipping-issue', 'order-issue', 'refund',
  'product-question', 'technical-issue'
];

const CHANNELS: Array<{ id: string; label: string; icon: LucideIcon }> = [
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'chat', label: 'Chat', icon: MessageSquare },
  { id: 'phone', label: 'Phone', icon: Phone },
  { id: 'sms', label: 'SMS', icon: Smartphone },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { id: 'web_form', label: 'Web Form', icon: Globe }
];

export function TicketSidebar({
  selectedView = 'inbox',
  onViewChange,
  selectedTags = [],
  onTagToggle,
  selectedChannel,
  onChannelChange,
  viewCounts,
  availableTags,
  collapsed = false,
  onToggleCollapse,
}: TicketSidebarProps) {
  const [tagsExpanded, setTagsExpanded] = useState(true);
  const [channelsExpanded, setChannelsExpanded] = useState(true);

  // Use available tags from ticket data, falling back to defaults
  const displayTags = availableTags && availableTags.length > 0
    ? availableTags
    : DEFAULT_TAGS;

  const getViewCount = (viewId: string): number => {
    if (!viewCounts) return 0;
    return viewCounts[viewId as keyof typeof viewCounts] || 0;
  };

  const getSmartViewCount = (viewId: string): number => {
    if (!viewCounts) return 0;
    return (viewCounts as Record<string, number>)[viewId] || 0;
  };

  const isSmartView = AI_SMART_VIEWS.some(v => v.id === selectedView);

  // Collapsed mode: thin icon bar — light theme
  if (collapsed) {
    return (
      <div className="w-11 bg-white/80 backdrop-blur-sm border-r border-gray-200/60 text-gray-600 h-full flex flex-col items-center py-2 flex-shrink-0">
        <button
          onClick={onToggleCollapse}
          className="p-2 mb-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Expand sidebar"
        >
          <PanelLeft size={18} />
        </button>

        <div className="w-7 border-t border-gray-200 mb-2" />

        {VIEWS.map((view) => {
          const Icon = view.icon;
          const isSelected = selectedView === view.id;
          const count = getViewCount(view.id);

          return (
            <button
              key={view.id}
              onClick={() => onViewChange?.(view.id)}
              className={`
                relative p-2 mb-1 rounded-lg transition-colors
                ${isSelected
                  ? 'bg-amber-50 text-amber-600'
                  : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
                }
              `}
              title={`${view.label}${count > 0 ? ` (${count})` : ''}`}
            >
              <Icon size={18} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 flex items-center justify-center px-1 rounded-full bg-amber-500 text-[10px] font-bold text-white">
                  {count}
                </span>
              )}
            </button>
          );
        })}

        {/* AI Smart Views — collapsed icons */}
        <div className="w-7 border-t border-purple-200 my-2" />
        {AI_SMART_VIEWS.map((view) => {
          const Icon = view.icon;
          const isSelected = selectedView === view.id;
          const count = getSmartViewCount(view.id);

          return (
            <button
              key={view.id}
              onClick={() => onViewChange?.(view.id)}
              className={`
                relative p-2 mb-1 rounded-lg transition-colors
                ${isSelected
                  ? 'bg-purple-50 text-purple-600'
                  : 'text-gray-400 hover:bg-purple-50/50 hover:text-purple-600'
                }
              `}
              title={`${view.label}${count > 0 ? ` (${count})` : ''}`}
            >
              <Icon size={16} />
              {count > 0 && (
                <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${view.dotColor}`} />
              )}
            </button>
          );
        })}
      </div>
    );
  }

  // Expanded mode: full sidebar — light theme with amber/purple accents
  return (
    <div className="w-56 bg-white/80 backdrop-blur-sm border-r border-gray-200/60 text-gray-700 h-full overflow-y-auto flex flex-col flex-shrink-0">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Tickets</h2>
        <button
          onClick={onToggleCollapse}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Collapse sidebar"
        >
          <PanelLeftClose size={18} />
        </button>
      </div>

      {/* Views */}
      <div className="py-2">
        {VIEWS.map((view) => {
          const Icon = view.icon;
          const isSelected = selectedView === view.id;
          const count = getViewCount(view.id);

          return (
            <button
              key={view.id}
              onClick={() => onViewChange?.(view.id)}
              className={`
                w-full flex items-center justify-between px-4 py-2 text-sm transition-colors
                ${isSelected
                  ? 'bg-amber-50/80 text-amber-900 font-medium border-l-[3px] border-l-amber-500'
                  : 'text-gray-600 hover:bg-gray-50 border-l-[3px] border-l-transparent'
                }
              `}
            >
              <div className="flex items-center gap-3">
                <Icon size={18} className={isSelected ? 'text-amber-600' : ''} />
                <span>{view.label}</span>
              </div>
              {count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  isSelected ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* AI Smart Views */}
      <div className="border-t border-gray-100">
        <div className="px-4 pt-3 pb-1.5 flex items-center gap-2">
          <Brain size={12} className="text-purple-500" />
          <span className="text-[10px] font-semibold text-purple-600 uppercase tracking-widest">AI Insights</span>
        </div>

        {AI_SMART_VIEWS.map((view) => {
          const Icon = view.icon;
          const isSelected = selectedView === view.id;
          const count = getSmartViewCount(view.id);

          return (
            <button
              key={view.id}
              onClick={() => onViewChange?.(view.id)}
              className={`
                w-full flex items-center justify-between px-4 py-1.5 text-[13px] transition-colors
                ${isSelected
                  ? 'bg-purple-50/80 text-purple-800 font-medium border-l-[3px] border-l-purple-500'
                  : 'text-gray-500 hover:bg-purple-50/40 hover:text-purple-700 border-l-[3px] border-l-transparent'
                }
              `}
            >
              <div className="flex items-center gap-2.5">
                <Icon size={15} className={isSelected ? 'text-purple-600' : 'text-gray-400'} />
                <span>{view.label}</span>
              </div>
              {count > 0 && (
                <span className={`min-w-[20px] h-5 flex items-center justify-center px-1.5 rounded-full text-[10px] font-bold ${
                  view.color === 'red'
                    ? 'bg-red-100 text-red-700'
                    : view.color === 'orange'
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-amber-100 text-amber-700'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tags Section */}
      <div className="border-t border-gray-100">
        <button
          onClick={() => setTagsExpanded(!tagsExpanded)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
        >
          <div className="flex items-center gap-2">
            {tagsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span>Tags</span>
          </div>
        </button>

        {tagsExpanded && (
          <div className="px-4 pb-2">
            {displayTags.map((tag) => {
              const isSelected = selectedTags.includes(tag);

              return (
                <button
                  key={tag}
                  onClick={() => onTagToggle?.(tag)}
                  className={`
                    w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors mb-1
                    ${isSelected
                      ? 'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                    }
                  `}
                >
                  <Tag size={12} />
                  <span>{tag}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Channels Section */}
      <div className="border-t border-gray-100">
        <button
          onClick={() => setChannelsExpanded(!channelsExpanded)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
        >
          <div className="flex items-center gap-2">
            {channelsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span>Channels</span>
          </div>
        </button>

        {channelsExpanded && (
          <div className="px-4 pb-2">
            {CHANNELS.map((channel) => {
              const Icon = channel.icon;
              const isSelected = selectedChannel === channel.id;

              return (
                <button
                  key={channel.id}
                  onClick={() => onChannelChange?.(channel.id)}
                  className={`
                    w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors mb-1
                    ${isSelected
                      ? 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                    }
                  `}
                >
                  <Icon size={14} />
                  <span>{channel.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
